import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandCategoriesDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrandCategories from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrandCategories";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Clickable, { ClickablePresentationProps } from "@store/MobiusOverrides/Clickable/Clickable";
import { gridGapStyles, linkStyles } from "@store/theme/common/mixins";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps } from "styled-components";

const enum fields {
    showImages = "showImages",
}
type OwnProps = HasCategoryContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const startCategoryId = props.category?.id.includes(':') ? props.category?.id.split(':')[0] : props.category?.id;
    const brandId = props.category?.id.includes(':') ? props.category?.id.split(':')[1] : "";

    const categoriesDataView = props.category?.id ? getCategoriesDataView(state, { maxDepth: 1, startCategoryId }) : undefined;
    const brandCategoriesDataView = props.category?.id ? getBrandCategoriesDataView(state, { brandId, page: 1, pageSize: 1000, sort: "name", maximumDepth: 4, }) : undefined;

    return {
        startCategoryId,
        brandId,
        categoriesDataView,
        brandCategoriesDataView,
    };
};

const mapDispatchToProps = {
    loadCategories,
    loadBrandCategories,
};

type Props = OwnProps & WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryDetailsSubCategoriesListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    subCategoryImageItem?: InjectableCss;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
}

export const categoryDetailsSubCategoriesListStyles: CategoryDetailsSubCategoriesListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 0,
        css: css`
            overflow-x: clip;
            ${gridGapStyles(
					["24px", "32px", null, "48px", "124px"], 
					["24px", "24px", null, "36px", "84px"]
				)
			}
        `,
    },
    categoryItem: {
        width: [12, 6, 4, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    innerContainer: {
        gap: 0,
        css: css`
            height: 100%;
            border: 1px solid ${getColor("common.border")};
            padding: 30px;
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 0.5em;
        `,
    },
    categoryNameLink: {
        css: css`
			${linkStyles("text", "primary", false)};
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.larger};
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    subCategoryNameLink: {
        typographyProps: {
            lineHeight: TYPOGRAPHY.lineHeight.large,
        },
        css: css`
            ${linkStyles("text.link", "text", false)}
			font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.large};
        `,
    },
    subCategoryImageItem: {
        css: css`
            width: 100%;
            height: inherit;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            position: relative;
            width: 100%;
            padding: 0;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["55%", "74%"], properties: ["padding-top"] }],
                    mediaOptions.min,
                )}

            &::before {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: linear-gradient(180deg, rgba(48, 48, 48, 0) 0%, rgba(48, 48, 48, 0.85) 100%);
                z-index: 1;
            }

            img {
                position: absolute;
                top: 50%;
                width: auto;
                max-height: 100%;
                max-width: none;
                transition: transform 1s cubic-bezier(0.215, 0.61, 0.355, 1);
                transform: translateY(-50%) scale(1);
            }
        `,
        errorTypographyProps: {
            css: css`
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
            `,
        },
    },
   
};

const styles = categoryDetailsSubCategoriesListStyles;

const findBrandCategory = (startCategoryId: string, dataView: BrandCategoryModel[] | null | undefined, i=0 ):BrandCategoryModel | undefined => {
    if (!dataView) {
        return undefined;
    }
    
    const foundCategory = dataView?.find(category => category.categoryId === startCategoryId);

    if (foundCategory) {
        return foundCategory;
    }

    for(let i=0; i<dataView.length; i++) {
        const brandCategory = findBrandCategory(startCategoryId, dataView[i].subCategories, i+1);
        if (brandCategory) {
            return brandCategory;
        }
    }

    return undefined;
}

const CategoryDetailsSubCategoriesList = ({
    startCategoryId,
    brandId,
    categoriesDataView,
    loadCategories,
    brandCategoriesDataView,
    loadBrandCategories,
    fields: { showImages },
    category: mainCategory,
}: Props) => {
    React.useEffect(() => {
        if(mainCategory) {
            const brandCategoriesParameter = {
                brandId: mainCategory?.id.split(':')[1],
                page: 1,
                pageSize: 1000,
                sort: "name",
                maximumDepth: 4,
            };

            if (!categoriesDataView?.isLoading && !categoriesDataView?.value) {
                loadCategories({ maxDepth: 1, startCategoryId: mainCategory?.id.split(':')[0] });
            }

            if (brandCategoriesParameter.brandId && !brandCategoriesDataView?.isLoading && !brandCategoriesDataView?.value) {
                loadBrandCategories(brandCategoriesParameter);
            }
        }
    }, [mainCategory]);

    if (!categoriesDataView || categoriesDataView?.isLoading || !brandCategoriesDataView || brandCategoriesDataView?.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!mainCategory || !mainCategory.subCategoryIds || mainCategory.subCategoryIds.length === 0 || !categoriesDataView?.value) {
        return null;
    }

    const brandSubCategories = findBrandCategory(startCategoryId || "", brandCategoriesDataView?.value)?.subCategories;

    const categories = brandSubCategories ? categoriesDataView.value?.filter((x) => {
        return brandSubCategories.some((y) => {
            return y.categoryId === x.id;
        });
    }) : categoriesDataView.value.filter(x => x.properties.isShopAll !== "true");

    return (
        <GridContainer {...styles.container}>
            {categories.map(category => (
                <GridItem key={category.id.toString()} {...styles.categoryItem}>
                    {showImages && (
                        <StyledWrapper {...styles.subCategoryImageItem}>
                            <CategoryImage 
                                categoryId={brandSubCategories ? `${category.id}:${brandId}` : category.id} 
                                categoryImage={category.smallImagePath}
                            />
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.innerContainer}>
                        <GridItem {...styles.categoryNameLinkItem}>
                            <CategoryLink 
                                categoryId={brandSubCategories ? `${category.id}:${brandId}` : category.id} 
                                isFirstLevel={true} 
                            />
                        </GridItem>
                        {!brandSubCategories && category.subCategoryIds?.map(subCategoryId => (
                            <GridItem key={subCategoryId} {...styles.subCategoryNameLinkItem}>
                                <CategoryLink categoryId={subCategoryId} />
                            </GridItem>
                        ))}
                    </GridContainer>
                </GridItem>
            ))}
        </GridContainer>
    );
}

interface LinkOwnProps { 
    categoryId: string;
    isFirstLevel?: boolean;
    categoryImage?: string;
}

const linkMapStateToProps = (state: ApplicationState, ownProps: LinkOwnProps) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

type CategoryLinkProps = LinkOwnProps & ReturnType<typeof linkMapStateToProps>;

const CategoryLinkView = (props: CategoryLinkProps) => {
	const { category, isFirstLevel } = props;

    if (!category) {
        return null;
    }

    const linkStyles = isFirstLevel ? styles.categoryNameLink : styles.subCategoryNameLink;

    return (
        <Link href={category.path} {...linkStyles} >
          {category.shortDescription}
        </Link>
    );
};

const CategoryLink = connect(linkMapStateToProps)(CategoryLinkView);

const CategoryImageView = (props: CategoryLinkProps) => {
	const { category, categoryImage } = props;

    if (!category) {
        return null;
    }

    return (
        <Clickable href={category.path} {...styles.subCategoryImageClickable}>
            <LazyImage
                src={categoryImage || category.smallImagePath ||  COMMON.defaultCategoryImagePath}
                altText={category.imageAltText}
                {...styles.subCategoryImage}
            />
        </Clickable>
    );
};

const CategoryImage = connect(linkMapStateToProps)(CategoryImageView);

const widgetModule: WidgetModule = {
    component: withCategory(connect(mapStateToProps, mapDispatchToProps)(CategoryDetailsSubCategoriesList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
